import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@mui/material';
import { generateColumnsDefaultConfiguration } from '../../../../components/dataGrid/columnHelpers';
import { GET_APPS } from '../../../../data/graphQL/queries/AdminRoles/getApps';
import { GET_APPS_ROL } from '../../../../data/graphQL/queries/AdminRoles/getAppsRol';
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';

export default function useRoleApps({ roleId, onError }) {
  const [catalogRows, setCatalogRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [draftRows, setDraftRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [getApps, { data: dataApps, error: errorApps, loading: loadingApps }] = useLazyQuery(GET_APPS, {
    fetchPolicy: 'cache-and-network'
  });

  const [getPantallaRol, { data: dataPantallaRol, error: errorPantallaRol, loading: loadingPantallaRol }] = useLazyQuery(GET_APPS_ROL, {
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    getApps();
  }, []);

  useEffect(() => {
    if (!dataApps?.apps?.success) return;
    setCatalogRows(dataApps.apps.valorEsperado || []);
  }, [dataApps]);

  useEffect(() => {
    if (errorApps) onError?.('ERRORDB');
  }, [errorApps, onError]);

  useEffect(() => {
    if (!roleId || roleId <= 0 || catalogRows.length === 0) return;
    getPantallaRol({ variables: { idRol: roleId } });
  }, [roleId, catalogRows, getPantallaRol]);

  useEffect(() => {
    if (!dataPantallaRol?.pantallaRol?.success) return;
    const rows = dataPantallaRol.pantallaRol.valorEsperado || [];
    setSelectedRows(rows);
    setDraftRows(rows);
  }, [dataPantallaRol]);

  useEffect(() => {
    if (errorPantallaRol) onError?.('ERRORDB');
  }, [errorPantallaRol, onError]);

  const selectedIds = useMemo(() => new Set(selectedRows.map((row) => row.idApp)), [selectedRows]);

  const availableRows = useMemo(() => {
    const unselected = catalogRows.filter((row) => !selectedIds.has(row.idApp));
    if (!searchValue?.trim()) return unselected;

    const value = searchValue.toLowerCase();
    return unselected.filter((row) => {
      const menu = (row.menu || '').toLowerCase();
      const descripcion = (row.descripcion || '').toLowerCase();
      return menu.includes(value) || descripcion.includes(value);
    });
  }, [catalogRows, selectedIds, searchValue]);

  const getCatalogField = (idApp, field) => {
    const row = catalogRows.find((item) => item.idApp === idApp);
    return row?.[field] || '';
  };

  const isSelected = (idApp) => draftRows.some((row) => row.idApp === idApp);
  const getDraftById = (idApp) => draftRows.find((row) => row.idApp === idApp);

  const toggleApp = (row) => {
    const exists = isSelected(row.idApp);
    if (exists) {
      setDraftRows((current) => current.filter((item) => item.idApp !== row.idApp));
      return;
    }

    setDraftRows((current) => [...current, { idApp: row.idApp, escritura: false, dataPrivacy: false }]);
  };

  const toggleEscritura = (row) => {
    const item = getDraftById(row.idApp);
    if (!item) {
      setDraftRows((current) => [...current, { idApp: row.idApp, escritura: true, dataPrivacy: false }]);
      return;
    }

    setDraftRows((current) => current.map((currentRow) => {
      if (currentRow.idApp !== row.idApp) return currentRow;
      return { ...currentRow, escritura: !item.escritura, dataPrivacy: false };
    }));
  };

  const toggleDataPrivacy = (row) => {
    const item = getDraftById(row.idApp);
    if (!item) {
      setDraftRows((current) => [...current, { idApp: row.idApp, escritura: false, dataPrivacy: true }]);
      return;
    }

    setDraftRows((current) => current.map((currentRow) => {
      if (currentRow.idApp !== row.idApp) return currentRow;
      return { ...currentRow, escritura: false, dataPrivacy: !item.dataPrivacy };
    }));
  };

  const removeSelected = (row) => {
    const filtered = selectedRows.filter((item) => item.idApp !== row.idApp);
    setSelectedRows(filtered);
    setDraftRows(filtered);
  };

  const openDialog = () => {
    setDraftRows(selectedRows);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDraftRows(selectedRows);
    setDialogOpen(false);
  };

  const confirmDialog = () => {
    setSelectedRows(draftRows);
    setDialogOpen(false);
  };

  const selectedColumns = useMemo(() => generateColumnsDefaultConfiguration([
    {
      field: 'menu',
      headerName: 'Menu',
      valueGetter: (_, row) => getCatalogField(row.idApp, 'menu')
    },
    {
      field: 'descripcion',
      headerName: 'Pantalla',
      valueGetter: (_, row) => getCatalogField(row.idApp, 'descripcion')
    },
    { field: 'escritura', type: 'boolean', headerName: 'Escritura' },
    { field: 'dataPrivacy', type: 'boolean', headerName: 'Data Privacy' },
    {
      field: 'accionesApp',
      headerName: 'Acciones',
      flex: 0.1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title='Eliminar'>
          <IconButton onClick={() => removeSelected(params.row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ]), [catalogRows, selectedRows]);

  const availableColumns = useMemo(() => generateColumnsDefaultConfiguration([
    { field: 'menu', headerName: 'Menu' },
    { field: 'descripcion', headerName: 'Pantalla' },
    { field: 'entrada', type: 'boolean', headerName: 'Visible' },
    {
      field: 'lectura',
      headerName: 'Lectura',
      flex: 0.1,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={isSelected(params.row.idApp)}
          onChange={() => toggleApp(params.row)}
        />
      )
    },
    {
      field: 'escritura',
      headerName: 'Escritura',
      flex: 0.1,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={Boolean(getDraftById(params.row.idApp)?.escritura)}
          onChange={() => toggleEscritura(params.row)}
        />
      )
    },
    {
      field: 'dataPrivacy',
      headerName: 'Data Privacy',
      flex: 0.1,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={Boolean(getDraftById(params.row.idApp)?.dataPrivacy)}
          onChange={() => toggleDataPrivacy(params.row)}
        />
      )
    }
  ]), [draftRows, availableRows]);

  return {
    selectedRows,
    availableRows,
    dialogOpen,
    selectedColumns,
    availableColumns,
    openDialog,
    closeDialog,
    confirmDialog,
    removeSelected,
    searchValue,
    setSearchValue,
    loading: loadingApps || loadingPantallaRol,
    error: errorApps || errorPantallaRol
  };
}
